import React from 'react';
import {Form} from 'semantic-ui-react';
import _ from 'lodash';
import LineItem from 'go/components/modules/requestDetails/v3/items/shared/LineItem';
import {
  calculateItemCostTotal,
  calculateItemActualCostTotal,
  calculateItemCategorizedCostTotal,
  calculateItemTotalPayment,
  getEditLabelFromEdits,
} from 'common/requestDetails/utils';
import MoreDetails from 'go/components/modules/requestDetails/v3/MoreDetails';
import SimpleInternal from 'go/components/modules/requestDetails/v3/items/shared/simple/SimpleInternal';
import InactiveAccordion from 'go/components/modules/requestDetails/v3/renderers/InactiveAccordion';
import ActiveAccordion from 'go/components/modules/requestDetails/v3/renderers/ActiveAccordion';
import {DeleteModal} from 'app-ui-componentlibrary';
import {MODES, CARD_TYPES, ACTUAL_ALLOWED_TOTAL} from 'common/requestDetails/constants';
import {SimpleHiringInternal} from './simple/SimpleHiringInternal';

const SimpleTypeToInternalMap = {
  [CARD_TYPES.SIMPLE]: SimpleInternal,
  [CARD_TYPES.SIMPLE_HIRING]: SimpleHiringInternal,
  [CARD_TYPES.STAFFING_BUNDLED]: SimpleHiringInternal,
};

/** Functionality in SimpleInternal **/
export const SimpleContent = (props) => {
  const ElementInternal =
    props.costType && SimpleTypeToInternalMap[props.costType]
      ? SimpleTypeToInternalMap[props.costType]
      : SimpleInternal;
  return (
    <LineItem {...props}>
      {(p) => (
        <React.Fragment>
          <div className="w-form">
            <Form>
              <ElementInternal
                item={p.item}
                handleBlur={p.handleBlur}
                handleChange={p.handleChange}
                handleItemChange={p.handleItemChange}
                handleItemChangeAndBlur={p.handleItemChangeAndBlur}
                editable={p.editable}
                beingCreated={p.beingCreated}
                id={p.id}
                itemConfig={p.itemConfig}
                itemCodeOptions={p.itemCodeOptions}
                originalItem={props.originalItem}
                period={p.period}
                openDeleteModal={p.openDeleteModal}
                closeDeleteModal={p.closeDeleteModal}
                validationErrors={p.validationErrors}
                validationDirtyMap={p.validationDirtyMap}
                sourcePath={p.sourcePath}
                rootPath={p.rootPath}
                mode={p.mode}
                displayFields={p.displayFields}
                costType={p.costType}
                costSectionHeader={p.costSectionHeader}
                pastTotalPayment={p.pastTotalPayment}
                additionalQuestionsEntityType={props.additionalQuestionsEntityType}
              />
            </Form>
          </div>
          {!p.editable &&
            !p.beingCreated &&
            (p.mode === MODES.APPLICATION ||
              _.includes(p.displayFields, 'additionalQuestions')) && (
              <MoreDetails
                attachedItemName={_.get(p.itemConfig, 'name')}
                moreDetailsConfig={_.get(
                  p.itemConfig,
                  'additionalQuestions.readOnlyContents'
                )}
                sourcePath={p.rootPath}
                tier={p.tier}
              />
            )}
        </React.Fragment>
      )}
    </LineItem>
  );
};

export default class SimpleShared extends React.Component {
  state = {
    isActive: this.props.addedActivityId === this.props.sourcePath || false,
    deleteItemModalOpen: false,
  };

  openDeleteModal = () => this.setState({deleteItemModalOpen: true});
  closeDeleteModal = () => this.setState({deleteItemModalOpen: false});

  handleExpand = () => {
    this.setState((prevState) => ({
      isActive: !prevState.isActive,
    }));
  };

  renderAccordion() {
    const {
      itemConfig,
      item,
      lineItem,
      deleteItem,
      sourcePath,
      editable,
      validationErrors,
      fundingOpportunityProgramId,
      moreDetailsURL,
      renderChangesSection,
      handleChange,
      handleBlur,
      handleItemChange,
      handleItemChangeAndBlur,
      handleItemChangeAndEditMode,
      handleIneligibleItemChangeAndBlur,
      handleNotFullyInScopeItemChangeAndBlur,
      toggleEditMode,
      editMode,
      mode,
      disableFields,
      displayFields,
      changesSectionClassName,
      originalItem,
    } = this.props;
    const {isActive} = this.state;
    let name = _.get(itemConfig, 'name', '');
    const itemCode = _.get(item, 'itemCode');
    const itemLabel = _.get(itemConfig, 'labels.itemLabel', 'item');
    const changes = _.get(item, 'additionalInformation.changes');
    const hasChanges = (changes && _.size(changes) > 0) || mode === MODES.PAYMENT_REQUEST;

    const ineligibleCheckName = 'ineligible';
    const ineligiblePath = 'additionalInformation.ineligible';
    const ineligible = _.get(lineItem, ineligiblePath, false);

    const deferredCheckname = 'deferred';
    const deferredPath = 'additionalInformation.deferred';
    const deferred = _.get(lineItem, deferredPath, false);

    const notFullyInScopeCheckname = 'notFullyInScope';
    const notFullyInScopePath = 'additionalInformation.notFullyInScope';
    const notFullyInScope = _.get(lineItem, notFullyInScopePath, false);

    const editLabel = getEditLabelFromEdits(changes);

    let isValid = true;
    const newlyCreated = _.find(
      _.get(item, 'additionalInformation.changes', []),
      (change) => change.type === 'newlyCreated'
    );
    const deletable =
      mode === MODES.APPLICATION ||
      (!!newlyCreated &&
        mode !== MODES.CLOSEOUT_REPORT &&
        mode !== MODES.CLOSEOUT_REPORT_REVIEW) ||
      (mode === MODES.CLOSEOUT_REPORT && !originalItem);

    _.forEach(_.get(validationErrors, 'error'), (err) => {
      isValid = isValid && err.isValid;
    });
    const closeoutMode =
      mode === MODES.CLOSEOUT_REPORT || mode === MODES.CLOSEOUT_REPORT_REVIEW;
    let actualTotal = null;
    if (closeoutMode) {
      actualTotal = calculateItemActualCostTotal(item);
    }

    const closeoutReviewMode = mode === MODES.CLOSEOUT_REPORT_REVIEW;
    let actualAllowedTotal = null;
    if (closeoutReviewMode) {
      actualAllowedTotal = calculateItemCategorizedCostTotal(
        item,
        ACTUAL_ALLOWED_TOTAL,
        true
      );
    }
    const canEdit =
      (mode === MODES.APPLICATION || closeoutMode || newlyCreated || editMode) &&
      editable;

    const headerOptions = mode === MODES.REVIEW || mode === MODES.CLOSEOUT_REPORT_REVIEW;

    if (itemCode === 8) {
      name = _.truncate(_.get(item, 'additionalInformation.explainOtherItem', ''), {
        length: _.get(itemConfig, 'headerItemMaxLength', 50),
      });
    }

    if (isActive) {
      return (
        <React.Fragment>
          <ActiveAccordion
            tier={2}
            isNarrative={false}
            handleToggle={this.handleExpand}
            total={
              mode === MODES.PAYMENT_REQUEST
                ? calculateItemTotalPayment(item)
                : calculateItemCostTotal(item)
            }
            actualTotal={actualTotal}
            actualAllowedTotal={actualAllowedTotal}
            headerTitle={`${itemLabel}: ${name}`}
            isValid={isValid}
            validationErrors={validationErrors}
            deleteItem={this.openDeleteModal}
            activityKey={'item-details'}
            activityName={name}
            itemLabel={itemLabel}
            editable={editable}
            editMode={editMode}
            newlyCreated={newlyCreated}
            deletable={deletable}
            handleItemChangeAndBlur={handleItemChangeAndBlur}
            handleIneligibleItemChangeAndBlur={handleIneligibleItemChangeAndBlur}
            handleNotFullyInScopeItemChangeAndBlur={
              handleNotFullyInScopeItemChangeAndBlur
            }
            lineItem={lineItem}
            toggleEditMode={toggleEditMode}
            handleItemChangeAndEditMode={handleItemChangeAndEditMode}
            programId={fundingOpportunityProgramId}
            editLabel={editLabel}
            headerOptions={headerOptions}
            moreDetailsURL={moreDetailsURL}
            name={name}
            ineligible={ineligible}
            ineligibleCheckName={ineligibleCheckName}
            ineligiblePath={ineligiblePath}
            notFullyInScope={notFullyInScope}
            notFullyInScopeCheckname={notFullyInScopeCheckname}
            notFullyInScopePath={notFullyInScopePath}
            deferred={deferred}
            deferredCheckName={deferredCheckname}
            deferredPath={deferredPath}
            disableFields={disableFields}
            displayFields={displayFields}
            mode={mode}
            sourcePath={sourcePath}
          >
            {editable && (
              <DeleteModal
                displayTitle={`Remove ${_.get(itemConfig, 'labels.itemLabel', 'item')}`}
                open={this.state.deleteItemModalOpen}
                cancelAction={this.closeDeleteModal}
                deleteAction={() => deleteItem(sourcePath)}
                displayText={'Are you sure you want to remove this item?'}
              />
            )}
            <SimpleContent
              canEdit={canEdit}
              handleChange={handleChange}
              handleBlur={handleBlur}
              handleItemChange={handleItemChange}
              handleItemChangeAndBlur={handleItemChangeAndBlur}
              lineItem={lineItem}
              {...this.props}
            />
          </ActiveAccordion>
          {hasChanges && (
            <div
              className={`app_accordion_budget-tier3${
                changesSectionClassName ? ' ' + changesSectionClassName : ''
              } w-clearfix`}
            >
              {renderChangesSection()}
            </div>
          )}
        </React.Fragment>
      );
    } else {
      return (
        <InactiveAccordion
          tier={2}
          handleToggle={this.handleExpand}
          total={
            mode === MODES.PAYMENT_REQUEST
              ? calculateItemTotalPayment(item)
              : calculateItemCostTotal(item)
          }
          actualTotal={actualTotal}
          actualAllowedTotal={actualAllowedTotal}
          headerTitle={`${itemLabel}: ${name}`}
          activityName={name}
          isValid={isValid}
          editable={editable}
          editLabel={editLabel}
          ineligible={ineligible}
          notFullyInScope={notFullyInScope}
          mode={mode}
          sourcePath={sourcePath}
        />
      );
    }
  }
  render() {
    const {
      beingCreated,
      lineItem,
      handleChange,
      handleBlur,
      handleItemChange,
      handleItemChangeAndBlur,
    } = this.props;
    return !beingCreated ? (
      this.renderAccordion()
    ) : (
      <SimpleContent
        canEdit={true}
        lineItem={lineItem}
        handleChange={handleChange}
        handleBlur={handleBlur}
        handleItemChange={handleItemChange}
        handleItemChangeAndBlur={handleItemChangeAndBlur}
        {...this.props}
      />
    );
  }
}
